<template>
  <div class="page page-lg">
    <site-header is-static />
    <v-main>
      <div
        v-if="!isLoading"
        class="app-content site-width"
      >
        <validation-observer
          ref="formValidationObserver"
          v-slot="formObserver"
          tag="div"
        >
          <!-- BREADCRUMBS -->
          <steps
            v-if="$vuetify.breakpoint.smAndDown"
            :steps="formMetadata.steps"
            :steps-observers="stepsObservers"
            :steps-passed="stepsPassed"
            :form-observer="formObserver"
          />

          <div class="app-content__spacer">
            <qz-cta
              class="page-lg-form__cta"
              :title="productTitleText"
              :current-step="1"
            />

            <how-it-works-redesign
              v-if="showHowItWorksRedesign"
            />
            <how-it-works
              v-else
              :companies="logos"
            />

            <!-- DO NOT REMOVE, WILL BE RE-ENABLED ONCE WE HAVE WLS VARIABLES FOR THESE SECTIONS -->
            <!-- <panel-of-providers class="mt-2" /> -->

            <div
              class="car-form d-block d-md-flex mt-2"
              sticky-container
            >
              <aside
                v-if="!isConfirmStepsShow"
                class="
                  car-form__sidebar
                  d-none
                  d-md-block
                "
              >
                <div v-sticky="$vuetify.breakpoint.mdAndUp">
                  <list-steps
                    :steps="formMetadata.steps"
                    :steps-observers="stepsObservers"
                    :steps-passed="stepsPassed"
                    :form-observer="formObserver"
                  />
                  <claims-form-link />
                </div>
              </aside>
              <div class="car-form__content ml-md-6">
                <form
                  id="form"
                  novalidate
                  class="form form-car"
                  @submit.prevent
                  @keydown.enter.prevent
                >
                  <form-plain
                    ref="form"
                    :is-confirm-steps="isConfirmStepsShow"
                    :session-data="journey"
                    :payload="payload"
                    :form-observer="formObserver"
                    :steps-passed="stepsPassed"
                    :is-edit-process="isEditProcess"
                    :is-continue-triggered="isContinueTriggered"
                    @data="onData"
                    @observer="stepsObservers[$event.stepId] = $event.observer"
                    @passed="onPassedStep"
                    @show-confirm-form="isConfirmStepsShow = $event"
                    @edit-process="isEditProcess = $event"
                    @reset-continue-trigger="isContinueTriggered = $event"
                  />

                  <!-- PREV/NEXT/SUBMIT BTNS -->
                  <div
                    v-show="showSubmit"
                    class="pa-4 px-md-0 form-actions text-right"
                  >
                    <div
                      ref="formActions"
                      v-sticky="$vuetify.breakpoint.lgAndUp && formObserver.valid"
                      class="form-actions__sticky-holder"
                      sticky-side="bottom"
                    >
                      <div class="holder">
                        <!-- NEXT STEP/SUBMIT BTN -->
                        <!-- RECAPTCHA -->
                        <template v-if="showCaptcha">
                          <vue-recaptcha
                            ref="recaptcha"
                            size="invisible"
                            :sitekey="config.captchaKey"
                            :load-recaptcha-script="true"
                            @verify="getQuotes(formObserver)"
                          />
                          <v-btn
                            color="success"
                            class="next-step-btn"
                            rounded
                            x-large
                            type="submit"
                            data-test="next_btn"
                            @click="validateBeforeRecaptcha(formObserver)"
                          >
                            <span>{{ submitButtonText }}</span>
                            <v-icon
                              dark
                              right
                              size="22"
                            >
                              mdi-arrow-right
                            </v-icon>
                          </v-btn>
                        </template>
                        <v-btn
                          v-else
                          color="success"
                          class="next-step-btn"
                          rounded
                          x-large
                          type="submit"
                          data-test="next_btn"
                          @click.prevent="getQuotes(formObserver)"
                        >
                          <span>{{ submitButtonText }}</span>
                          <v-icon
                            dark
                            right
                            size="22"
                          >
                            mdi-arrow-right
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </validation-observer>
      </div>

      <quote-limit-exceed-popup v-model="quoteLimitExceeded" />

      <!-- FOOTER -->
      <site-footer
        v-if="config.partnerFooter && !isLoading"
        role="footer"
      >
        <div v-html="config.partnerFooterText || ''" />
      </site-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <bespoke-footer-information
        v-if="showBespokeFooterInformation"
        :content="config.bespokeFooterInformation"
      />
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="isLoading || isProcessing" />
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useLeadgenForm from '@/composables/useLeadgenForm';
import {
  createAbtastyScript,
  removeAbtastyScript,
} from '@/helpers/common';
import Steps from '@/views/LG/Steps';
import HowItWorks from '@/views/LG/components/HowItWorks';
import HowItWorksRedesign from '@/views/LG/components/HowItWorksRedesign';
// import PanelOfProviders from '@/views/LG/components/PanelOfProviders';
import ListSteps from '@/views/LG/components/ListSteps.vue';
import FormPlain from '@/views/LG/components/FormPlain';
import QzCta from '@/components/QzCta/QzCta.vue';
import ClaimsFormLink from '@/components/ClaimsFormLink.vue';

export default {
  name: 'page-insurance',
  inject: [
    'config',
    'formMetadata',
    'constants',
  ],
  metaInfo () {
    const defaultTitle = `${this.config.partnerText} | ${this.formMetadata.productTitle} Insurance`;
    return {
      title: this.config.partnerPageFormTitle || defaultTitle,
    };
  },
  components: {
    VueRecaptcha,
    ListSteps,
    Steps,
    HowItWorks,
    HowItWorksRedesign,
    // PanelOfProviders,
    FormPlain,
    QzCta,
    ClaimsFormLink,
  },
  computed: {
    showSubmit () {
      const steps = Object.values(this.stepsPassed);
      steps.pop();
      return steps.every(stepPassed => stepPassed);
    },
    productTitleText () {
      if (this.productName === 'coach') return 'Coach & Bus Insurance';
      if (this.productName === 'incomeprotection') return this.formMetadata.productTitle;

      return `${this.formMetadata.productTitle} Insurance`;
    },
    submitButtonText () {
      if (['life', 'incomeprotection'].includes(this.productName)) return 'Accept and find cover';

      return 'Accept and get quotes';
    },
    showHowItWorksRedesign () {
      return ['life', 'incomeprotection'].includes(this.productName);
    },
  },
  async created () {
    await this.init();
    await this.gtmTracking('form');
    createAbtastyScript(this.config);
  },
  beforeDestroy () {
    removeAbtastyScript(this.config);
  },
  setup (props, context) {
    return {
      ...useQuotezoneCommon(null, context),
      ...useLeadgenForm(null, context),
    };
  },
  methods: {
    onData ({ fields, stepId }) {
      Object.keys(fields).forEach(field => {
        if (this.formMetadata.singleStep) {
          this.$set(this.payload, field, fields[field]);
        } else {
          if (!this.payload[stepId]) this.payload[stepId] = {};
          this.$set(this.payload[stepId], field, fields[field]);
        }
      });
    },
    onPassedStep (stepId) {
      // Need to check for stepId presence in stepsPassed, because onPassedStep is called also for confirmSteps
      if (Object.prototype.hasOwnProperty.call(this.stepsPassed, stepId)) this.stepsPassed[stepId] = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.page-lg {
  .app-content {
    &__spacer {
      padding-top: _percentage(35, $site-width);
    }
  }
}

.page-lg-form {
  &__cta {
    & + * {
      margin-top: _percentage(35, $site-width);
    }
  }
}

.car-form {
  display: flex;
  align-items: flex-start;
  position: relative;

  &__content {
    flex: 1;
  }

  &__sidebar {
    flex: 0 0 190px;
    width: 190px;
  }
}
</style>

<style lang="scss" scoped src="@/assets/scss/core-products/product-insurance.scss"/>
<style lang="scss" scoped src="@/assets/scss/page-width.scss"/>
